/**
 * Utilidades helper para el widget TIA GPC
 */

import { BREAKPOINTS } from './constants';

/**
 * Verifica si el dispositivo es móvil
 * @returns {boolean}
 */
export const isMobile = () => {
  return window.innerWidth < BREAKPOINTS.MOBILE;
};

/**
 * Verifica si el dispositivo es tablet
 * @returns {boolean}
 */
export const isTablet = () => {
  return window.innerWidth >= BREAKPOINTS.MOBILE && window.innerWidth < BREAKPOINTS.TABLET;
};

/**
 * Formatea un timestamp ISO a formato legible
 * @param {string} isoString - Timestamp en formato ISO
 * @returns {string} Formato legible (HH:MM)
 */
export const formatTimestamp = (isoString) => {
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '';
  }
};

/**
 * Formatea milisegundos a formato legible
 * @param {number} ms - Milisegundos
 * @returns {string} Formato legible (ej: "1.5s", "234ms")
 */
export const formatResponseTime = (ms) => {
  if (ms >= 1000) {
    return `${(ms / 1000).toFixed(1)}s`;
  }
  return `${Math.round(ms)}ms`;
};

/**
 * Genera un ID único
 * @returns {string} UUID v4
 */
export const generateId = () => {
  return crypto.randomUUID();
};

/**
 * Clasifica el error HTTP y devuelve mensaje traducible
 * @param {number} statusCode - Código de estado HTTP
 * @returns {string} Clave de traducción para el error
 */
export const getErrorKey = (statusCode) => {
  switch (statusCode) {
    case 401:
      return 'errors.session';
    case 403:
      return 'errors.validation';
    case 429:
      return 'errors.rateLimit';
    case 500:
    case 502:
    case 503:
      return 'errors.network';
    default:
      return 'errors.generic';
  }
};

/**
 * Obtiene el tema del sistema operativo
 * @returns {'light'|'dark'} Tema del sistema
 */
export const getSystemTheme = () => {
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
};

/**
 * Sanitiza el input del usuario para prevenir inyección
 * @param {string} input - Input del usuario
 * @returns {string} Input sanitizado
 */
export const sanitizeInput = (input) => {
  return input.trim().slice(0, 2000);
};

/**
 * Valida si una string es un UUID válido
 * @param {string} uuid - UUID a validar
 * @returns {boolean}
 */
export const isValidUUID = (uuid) => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

/**
 * Debounce function para optimizar eventos frecuentes
 * @param {Function} func - Función a debounce
 * @param {number} wait - Tiempo de espera en ms
 * @returns {Function}
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};
